import { Dialog, DialogSurface, DialogBody, DialogTitle, Button, DialogContent, Label, DialogActions, Tab, TabList, Textarea, Field, RadioGroup, Radio, Rating, tokens } from "@fluentui/react-components";
import { Dismiss24Filled, EditRegular } from "@fluentui/react-icons";
import React from "react";
import { processServerError } from "../../utils/helpers/error.helper";
import * as yup from 'yup';
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import "./login-questionnaire-modal.styless.scss";
import { questionnaireMap } from "./questionsMap";
import { LoginQuestionnairePostModel } from "../../swagger-clients/s365-dashboard-v2-api-clients.service";
import { getQuestionnairesClient } from "../../services/dashboard.service";
import { toast } from "react-toastify";

type LoginQuestionnaireModalProps = {
    isOpened?: boolean;
    onClose?: () => void;
}

const schema = yup.object().shape({
    reason: yup.string().required('This question is required!'),
    usedDWSIM: yup.string().required('This question is required!'),
    dwsimRating: yup.number().when('usedDWSIM', {
        is: 'yes',
        then: (schema) => schema.required('Rating is required!').moreThan(0, 'Rating is required!'),
        otherwise: (schema) => schema.notRequired(),
    }),
    workflow: yup.string().required('This question is required!'),
    protectProperty: yup.string().required('This question is required!')
});

type FormData = yup.InferType<typeof schema>;

export const LoginQuestionnaireModal: React.FC<LoginQuestionnaireModalProps> = (props) => {
    const [open, setOpen] = React.useState(false);
    const [isLoading, setIsLoading] = React.useState<boolean>(false);
    const {
        control,
        handleSubmit,
        watch,
        formState: { errors },
    } = useForm<FormData>({
        resolver: yupResolver(schema),
        defaultValues: {
            reason: '',
            usedDWSIM: 'yes',
            dwsimRating: 0,
            workflow: '',
            protectProperty: ''
        },
    });

    React.useEffect(() => {
        setOpen(!!props.isOpened);
    }, [props.isOpened]);


    const onModalClose = () => {
        setOpen(false);

        if (props.onClose) {
            props.onClose();
        }

    }



    const usedDWSIM = watch('usedDWSIM');

    const onSubmit = async (data: FormData) => {
        console.log('Form Data:', data);
        try {
            setIsLoading(true);
            const usedDwsimFree = data.usedDWSIM == "yes" ? true : false;
            const dwsimRank = usedDwsimFree ? `dwsimFreeRank_${data.dwsimRating}` : undefined;

            const model = new LoginQuestionnairePostModel({
                signupReason: data.reason,
                dwsimRank: dwsimRank,
                usedDwsimFree: usedDwsimFree ? "usedDwsimFree_yes" : "usedDwsimFree_no",
                howYouWorkOnFlowsheets: data.workflow,
                protectProperty: data.protectProperty
            });

            const client = getQuestionnairesClient();
            await client.submitLoginQuestionnaireAnswers(model);
            toast.success("Thank you for your feedback! Your response has been submitted.");
            props.onClose();


        } catch (error) {
            processServerError(error, undefined, "An error occurred while submitting answers.");
        } finally {
            setIsLoading(false);
        }


    };


    return <Dialog modalType="alert" open={open} onOpenChange={(event, data) => {

    }}>
        <DialogSurface>
            <DialogBody style={{ maxWidth: "inherit" }}>
                <DialogTitle>
                    What brings you here?
                </DialogTitle>
                <DialogContent>

                    <Field label={<b>1. Why did you sign up?</b>}
                        validationState={errors.reason ? 'error' : undefined}
                        validationMessage={errors.reason ? errors.reason.message : undefined}
                        style={{ marginBottom: tokens.spacingVerticalMNudge }}>
                        <Controller
                            control={control}
                            name="reason"
                            render={({ field }) => (
                                <RadioGroup {...field} disabled={isLoading}>
                                    <Radio value="signupReason_comparePrices" label="To compare prices" />
                                    <Radio value="signupReason_simulationTask" label="I have a simulation task/project" />
                                    <Radio value="signupReason_betterThanFree" label="DWSIM Pro seems better than DWSIM" />
                                    <Radio value="signupReason_alternative" label="I am looking for an alternative to a commercial simulator" />
                                    <Radio value="signupReason_none" label="None of the above" />
                                </RadioGroup>
                            )}
                        />
                    </Field>

                    <Field label={<b>2. Have you used DWSIM open source?</b>}
                        validationState={errors.usedDWSIM ? 'error' : undefined}
                        validationMessage={errors.usedDWSIM ? errors.usedDWSIM.message : undefined}
                        style={{ marginBottom: tokens.spacingVerticalMNudge }}
                    >
                        <Controller
                            control={control}
                            name="usedDWSIM"
                            render={({ field }) => (
                                <RadioGroup {...field} disabled={isLoading}>
                                    <Radio value="yes" label="Yes - it can simulate everything I need" />
                                    <Radio value="no" label="No, I've never used it" />
                                </RadioGroup>
                            )}
                        />
                    </Field>

                    {usedDWSIM === 'yes' && (
                        <Field label={<b>2a. How would you rate it?</b>}
                            validationState={errors.dwsimRating ? 'error' : undefined}
                            validationMessage={errors.dwsimRating ? errors.dwsimRating.message : undefined}
                            style={{ marginBottom: tokens.spacingVerticalMNudge }}>
                            <Controller
                                control={control}
                                name="dwsimRating"
                                render={({ field }) => (
                                    <Rating
                                        value={field.value}
                                        color="marigold"
                                        className={isLoading ? 'disabled-rating' : ''}
                                        onChange={isLoading ? undefined : field.onChange}
                                    />
                                )}
                            />
                        </Field>
                    )}

                    <Field label={<b>3. How do you typically work with flowsheets?</b>}
                        validationState={errors.workflow ? 'error' : undefined}
                        validationMessage={errors.workflow ? errors.workflow.message : undefined}
                        style={{ marginBottom: tokens.spacingVerticalMNudge }}>
                        <Controller
                            control={control}
                            name="workflow"
                            render={({ field }) => (
                                <RadioGroup {...field} disabled={isLoading}>
                                    <Radio value="howYouWorkOnFlowsheets_alone" label="I work alone on simulation projects" />
                                    <Radio value="howYouWorkOnFlowsheets_smallTeam" label="I work as part of a small team (2-5 people)" />
                                    <Radio value="howYouWorkOnFlowsheets_largeTeam" label="I'm in a larger team (6+ simulation experts)" />
                                    <Radio value="howYouWorkOnFlowsheets_external" label="I collaborate with external partners (e.g., clients, academic groups)" />
                                </RadioGroup>
                            )}
                        />
                    </Field>

                    <Field label={<b>4. Do you work under Non-Disclosure Agreement (NDA)?</b>}
                        validationState={errors.protectProperty ? 'error' : undefined}
                        validationMessage={errors.protectProperty ? errors.protectProperty.message : undefined}
                        style={{ marginBottom: tokens.spacingVerticalMNudge }}>
                        <Controller
                            control={control}
                            name="protectProperty"
                            render={({ field }) => (
                                <RadioGroup {...field} disabled={isLoading}>
                                    <Radio value="protectProperty_No" label="No" />
                                    <Radio value="protectProperty_Concerned" label="I am concerned about protecting my flowsheet's intellectual property." />
                                    <Radio value="protectProperty_IT_department" label="My IT department deals with this, e.g., CLOUD Act." />
                                    <Radio value="protectProperty_not_concerned" label="My company is not concerned about protecting my flowsheet's intellectual property. " />
                                </RadioGroup>
                            )}
                        />
                    </Field>


                </DialogContent>
                <DialogActions>
                    <Button appearance="primary" disabled={isLoading} onClick={handleSubmit(onSubmit)}>Submit</Button>
                </DialogActions>
            </DialogBody>
        </DialogSurface>
    </Dialog >
}